import { createInitializerQueueList } from './initializer.manager';
import { EnvironmentProviders, inject, Injector, provideAppInitializer } from '@angular/core';
import { DecoratorService } from '../helpers/decorator-service.service';
import { GlobalEventsService } from '../services/global-events.service';
import { GoogleTagManagerService } from '../services/google-tag-manager.service';
import { PwaService } from '../services/pwa.service';
import { UserPaymentModeService } from '../services/user/user-payment-mode.service';
import { SeoService } from '../services/seo.service';
import { WebSocketService } from '../services/web-socket.service';
import { ThemeService } from '../services/theme.service';
import { ZendeskChatService } from '../services/zendesk/zendesk-chat.service';
import { TelegramAppService } from '../services/telegram-app.service';
import { NotificationCenterService } from '../shared/modules/notification-center/notification-center.service';
import { UbidexService } from '../services/ubidex.service';
import {
  CustomNotificationCenterService,
} from '../shared/modules/notification-center/custom-notification-center.service';
import { DataPreloaderService } from '../services/data-preloader.service';
import { GameProvidersService } from '../services/games/game-providers.service';
import { ContentUpdaterService } from '../services/content-updater/content-updater.service';
import { UserPhoneService } from '../services/user/user-phone.service';
import { SocketSuccessHandlerService } from '../services/socket-success-handler.service';
import { FraudCheckService } from '../services/fraud-check.service';
import { UserTransactionsService } from '../services/user/user-transactions.service';
import { PromotionResolverService } from '../../page/promotion/promotion-resolver.service';
import { UserBonusesService } from '../services/user/user-bonuses.service';
import { ActiveBannerService } from '../services/active-banner.service';
import { CustomerIoService } from '../services/customer-io.service';
import { CustomBonusService } from '../services/custom-bonus.service';
import { RecaptchaService } from '../services/recaptcha.service';
import { HotjarService } from '../services/hotjar.service';
import { PersonalizedBannerService } from '../services/personalized-banner.service';
import { GameCategoryAttrService } from '../services/games/game-category-attribute.service';
import { BankingService } from '../services/banking.service';
import { LazySentryService } from '../services/lazy-sentry.service';

/**
 * The `initApp` function initializes the DecoratorService with the provided Injector.
 * @param {Injector} injector - The `injector` parameter in the `initApp` function is of type
 * `Injector`. It is used to provide dependencies to the application components. The `Injector` is a
 * dependency injection container that resolves dependencies for classes and services in Angular
 * applications.
 * @returns A function is being returned.
 */
export function initApp(injector: Injector) {
  DecoratorService.injector = injector;
}

export const provideInitializer = () => {
  return [
    provideAppInitializer(() => {
      const injector = inject(Injector);
      initApp(injector);
      return Promise.resolve();
    }),
    ...parallelAppInitializer,
  ];
};

const parallelAppInitializer: EnvironmentProviders[] = createInitializerQueueList([
  {
    token: DataPreloaderService,
    initializerFn: (service: DataPreloaderService) => service.preloadCriticalData(),
  },
  {
    token: PromotionResolverService,
    initializerFn: (service: PromotionResolverService) => service.resolvePromos(),
  },
  {
    token: GlobalEventsService,
    initializerFn: (service: GlobalEventsService) => service.initGlobalEvent(),
  },
  {
    token: GoogleTagManagerService,
    initializerFn: (service: GoogleTagManagerService) => service.addGtmToDom(),
  },
  {
    token: PwaService,
    initializerFn: (service: PwaService) => service.registerServiceWorker(),
  },
  {
    token: UserPaymentModeService,
    initializerFn: (service: UserPaymentModeService) => service.initPaymentMode(),
  },
  {
    token: UserPhoneService,
    initializerFn: (service: UserPhoneService) => service.updatePhoneStoreMask(),
  },
  {
    token: SeoService,
    initializerFn: (service: SeoService) => service.updateGlobalHeadLink(),
  },
  {
    token: ThemeService,
    initializerFn: (service: ThemeService) => service.resolveTheme$(),
  },
  {
    token: ContentUpdaterService,
    initializerFn: (service: ContentUpdaterService) => service.runContentChangeHandler(),
  },
  {
    token: GameProvidersService,
    initializerFn: (service: GameProvidersService) => service.updateProviderListOnLangChange(),
  },
  {
    token: ZendeskChatService,
    initializerFn: (service: ZendeskChatService) => service.initChat(),
  },
  {
    token: TelegramAppService,
    initializerFn: (service: TelegramAppService) => service.detectTelegramApp(),
  },
  {
    token: CustomNotificationCenterService,
    initializerFn: (service: CustomNotificationCenterService) => service.initCustomNotify(),
  },
  {
    token: NotificationCenterService,
    initializerFn: (service: NotificationCenterService) => service.initNotify(),
  },
  {
    token: UbidexService,
    initializerFn: (service: UbidexService) => service.initUbidex(),
  },
  {
    token: WebSocketService,
    initializerFn: (service: WebSocketService) => service.createConnection(),
  },
  {
    token: SocketSuccessHandlerService,
    initializerFn: (service: SocketSuccessHandlerService) => service.initHandler(),
  },
  {
    token: FraudCheckService,
    initializerFn: (service: FraudCheckService) => service.initFraudCheck(),
  },
  {
    token: UserTransactionsService,
    initializerFn: (service: UserTransactionsService) => service.transactionListAuthed(),
  },
  {
    token: UserBonusesService,
    initializerFn: (service: UserBonusesService) => service.loadBonusList(),
  },
  {
    token: ActiveBannerService,
    initializerFn: (service: ActiveBannerService) => service.initialize(),
  },
  {
    token: CustomerIoService,
    initializerFn: (service: CustomerIoService) => service.load(),
  },
  {
    token: LazySentryService,
    initializerFn: (service: LazySentryService) => service.initSentry(),
  },
  {
    token: CustomBonusService,
    initializerFn: (service: CustomBonusService) => service.initCustomBonuses(),
  },
  {
    token: RecaptchaService,
    initializerFn: (service: RecaptchaService) => service.initCaptcha(),
  },
  {
    token: HotjarService,
    initializerFn: (service: HotjarService) => service.initHotjar(),
  },
  {
    token: PersonalizedBannerService,
    initializerFn: (service: PersonalizedBannerService) => service.init(),
  },
  {
    token: GameCategoryAttrService,
    initializerFn: (service: GameCategoryAttrService) => service.updateCategoryAttrListOnLangChange(),
  },
  {
    token: BankingService,
    initializerFn: (service: BankingService) => service.getAllPayments(),
  },
]);
